import { useParams } from 'react-router-dom'
import { useUser } from '@/modules/shared/context/UserContext'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import { RolePermission } from '@/modules/shared/types/account'

/**
 * The set of per-company capability flags a role can grant. These mirror the
 * `canX` / `isDriver` booleans on {@link RolePermission} and are the same flags
 * the legacy `app/` modules gated on (ported via the old roleService rules).
 *
 * Admin status (globalAdmin / paymentAdmin / companyAdmin / demoCompanyAdmin)
 * is NOT a permission flag — it lives on the account and is exposed through the
 * `is*Admin` helpers on {@link AccessContext}.
 */
export type PermissionFlag =
  | 'canViewActivities'
  | 'canEditActivities'
  | 'canViewBillingData'
  | 'canEditBillingData'
  | 'canViewDelegations'
  | 'canEditDelegations'
  | 'canViewAlerts'
  | 'canEditAlerts'
  | 'canImportFiles'
  | 'canExportData'
  | 'canGeneratePrintouts'
  | 'canGenerateAttestation'
  | 'canEditActivityDay'
  | 'canEditGpsData'
  | 'canAddDriver'
  | 'canViewConfiguration'
  | 'canReadDatabaseLogin'
  | 'canCreateCompany'
  | 'canEditCompany'
  | 'canRemoveCompany'
  | 'canCreateUser'
  | 'canEditUser'
  | 'canRemoveUser'
  | 'isDriver'

/**
 * Everything a gate decision can need about the current user in the context of
 * a single company. Built once from {@link useUser} + {@link usePermissionChecker}
 * and handed to permission predicates so rules can be expressed declaratively.
 */
export interface AccessContext {
  /** Company the decision is scoped to (resolved from the route when omitted). */
  companyId: string
  /** True when the user's roles in this company grant the given flag. */
  can: (flag: PermissionFlag) => boolean
  /** True when ANY of the flags is granted. */
  canAny: (flags: PermissionFlag[]) => boolean
  /** True when ALL of the flags are granted. */
  canAll: (flags: PermissionFlag[]) => boolean
  /** True when the user holds at least one role in this company. */
  hasAnyRole: boolean
  /** The resolved RolePermission objects for this company. */
  rolesInCompany: RolePermission[]
  isAdmin: () => boolean
  isPaymentAdmin: () => boolean
  isCompanyAdmin: () => boolean
  isDemoCompanyAdmin: () => boolean
  /** Any of the four admin variants. */
  isAnyAdmin: () => boolean
  /** Back-office user (company/user-management flags) and not an admin. */
  isManager: () => boolean
  /** Pure self-service driver in this company and not an admin. */
  isDriver: () => boolean
}

/** A custom access rule. When provided to {@link AccessConfig} it is the sole authority. */
export type AccessPredicate = (ctx: AccessContext) => boolean

export interface AccessConfig {
  /**
   * Required permission flag(s). With `mode: 'any'` (default) the user needs at
   * least one; with `mode: 'all'` they need every flag. Ignored when `require`
   * is supplied.
   */
  permissions?: PermissionFlag | PermissionFlag[]
  /** How `permissions` combine. Default `'any'`. */
  mode?: 'any' | 'all'
  /**
   * When true (default), any admin (global / payment / company / demo) bypasses
   * the `permissions` check. Set to `false` for operational surfaces the legacy
   * app hid from admins (driver/vehicle/import/export/printout/timetable).
   * Ignored when `require` is supplied — a predicate gets full context and
   * decides admin handling itself.
   */
  allowAdmin?: boolean
  /**
   * Escape hatch for rules that need role helpers or negations
   * (e.g. `!isDriver() && canAny([...])`). When set, it fully owns the decision
   * and `permissions` / `mode` / `allowAdmin` are ignored.
   */
  require?: AccessPredicate
}

/**
 * Builds the {@link AccessContext} for the current user in `companyId`
 * (falling back to the `:companyId` route param). Always returns a context;
 * `isLoading` is true until the account + roles queries resolve, during which
 * all permission checks read as denied — callers should show a loader, not the
 * no-permission screen, while loading.
 */
export function useAccessContext(companyId?: string): {
  ctx: AccessContext
  isLoading: boolean
} {
  const params = useParams<{ companyId?: string }>()
  const resolvedCompanyId = companyId ?? params.companyId ?? ''
  const user = useUser()
  const can = usePermissionChecker(resolvedCompanyId)

  const rolesInCompany = resolvedCompanyId
    ? user.getRolesInCompany(resolvedCompanyId)
    : []

  // "Member of this company" — true if the user has a resolved RolePermission
  // OR a raw role assignment whose name didn't resolve against the roles config.
  // We fall back to the raw assignment so a name mismatch never locks a genuine
  // member out of identity-level surfaces (e.g. the dashboard landing).
  const hasAnyRole =
    rolesInCompany.length > 0 ||
    (resolvedCompanyId !== '' &&
      user.getRoleNamesInCompany(resolvedCompanyId) !== '')

  const ctx: AccessContext = {
    companyId: resolvedCompanyId,
    can: (flag) => can(flag as keyof RolePermission),
    canAny: (flags) => flags.some((flag) => can(flag as keyof RolePermission)),
    canAll: (flags) => flags.every((flag) => can(flag as keyof RolePermission)),
    hasAnyRole,
    rolesInCompany,
    isAdmin: user.isAdmin,
    isPaymentAdmin: user.isPaymentAdmin,
    isCompanyAdmin: user.isCompanyAdmin,
    isDemoCompanyAdmin: user.isDemoCompanyAdmin,
    isAnyAdmin: () =>
      user.isAdmin() ||
      user.isPaymentAdmin() ||
      user.isCompanyAdmin() ||
      user.isDemoCompanyAdmin(),
    isManager: () => user.isManager(resolvedCompanyId),
    isDriver: () => user.isDriver(resolvedCompanyId),
  }

  return { ctx, isLoading: user.isLoading }
}

/** Pure evaluation of an {@link AccessConfig} against a resolved context. */
export function evaluateAccess(ctx: AccessContext, config: AccessConfig): boolean {
  const { permissions, mode = 'any', allowAdmin = true, require } = config

  if (require) return require(ctx)

  // Admin bypass for declarative permission gates — any admin variant. A pure
  // driver is never an admin, so this never weakens driver restrictions.
  if (allowAdmin && ctx.isAnyAdmin()) return true

  if (permissions == null) return true
  const flags = Array.isArray(permissions) ? permissions : [permissions]
  if (flags.length === 0) return true

  return mode === 'all' ? ctx.canAll(flags) : ctx.canAny(flags)
}

/**
 * Inline access check for use anywhere in a component.
 *
 * ```ts
 * const { allowed, isLoading } = useHasAccess({ permissions: 'canEditBillingData' })
 * if (allowed) return <EditButton />
 * ```
 *
 * While `isLoading` is true, `allowed` is `false` — render a loader rather than
 * hiding the feature, so it doesn't flash as denied before auth data arrives.
 */
export function useHasAccess(
  config: AccessConfig,
  companyId?: string,
): { allowed: boolean; isLoading: boolean } {
  const { ctx, isLoading } = useAccessContext(companyId)
  if (isLoading) return { allowed: false, isLoading: true }
  return { allowed: evaluateAccess(ctx, config), isLoading: false }
}
